import AddIcon from '@mui/icons-material/Add';
import UnfoldMoreOutlinedIcon from '@mui/icons-material/UnfoldMoreOutlined';
import { Grid, Typography, styled, Box } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  deleteByIdBusinessDefinition,
  getAllBusinessDefinition,
  saveBusinessDefinition,
  updateArchiveByIdBusinessDefinition,
  BUOrder,
  BUUpdateOrderReq,
  updateBusinessDefinitionOrderById,
} from 'wisestep-api';
import { EmptyTeamsAndUsersIcon, Label, WsButton, WsColors } from 'wisestep-base';

import NoDataFoundCard from '../../../components/noDataFoundCard';
import { Loader } from '../../../features/auth/components/OtpPageLayout';
import StageStatusContextProvider from '../../../features/submissions/context/StageStatusContext';
import { success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { IBusinessData, IStageData } from '../customizeStageStatus/helper';

import BusinessDefinitionCard from './BusinessDefinitionCard';
import BusinessStageStatusPopUp from './BusinessStageStatusPopUp';
import OrderBUDefinition from './OrderBUDefinition';
export const StatusTypography = styled(Typography)(() => ({
  backgroundColor: WsColors.TheBlues100,
  padding: 8,
  width: 200,
  borderRadius: 4,
}));

const CustomizeBusinessDefinition = () => {
  const [openStatusModal, setOpenStatusModal] = useState<boolean>(false);
  const [openSubStatus, setOpenSubStatus] = useState<boolean>(false);
  const [openBusinessModal, setOpenBusinessModal] = useState<boolean>(false);
  const [businessData, setBusinessData] = useState<IBusinessData>();
  const [allBusinessDefinition, setAllBusinessDefinition] = useState<IBusinessData[]>();
  const [businessName, setBusinessName] = useState<string>('');
  const [openCardIndex, setOpenCardIndex] = useState(null);
  const [stageData, setStageData] = useState<IStageData>();
  const [onEdit, setOnEdit] = useState(-1);
  const [onEditArchived, setOnEditArchived] = useState<number>(-1);
  const [loader, setLoader] = useState<boolean>(false);
  const [jobOption, setJobOption] = useState<string>('');
  const [isRename, setIsRename] = useState<boolean>(false);
  const [openReArrange, setOpenReArrange] = useState<boolean>(false);
  const setNotification = useNotificationStore((state) => state.setNotification);

  const methods = useForm();
  const { watch } = methods;
  const handleToggle = (index) => {
    setOpenCardIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  const fetchAllBusinessDefinition = useCallback(async () => {
    setLoader(true);
    const response = await getAllBusinessDefinition();
    setAllBusinessDefinition(
      response.data
        ? response.data
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .slice()
            .sort((a, b) => {
              return a.isArchive === b.isArchive ? 0 : a.isArchive ? 1 : -1;
            })
        : []
    );
    methods.reset();
    setLoader(false);
  }, [methods]);

  useEffect(() => {
    fetchAllBusinessDefinition();
  }, [fetchAllBusinessDefinition]);

  const onSubmit = async (isArchive) => {
    if (onEdit == -1 || isArchive) {
      businessData && (await saveBusinessDefinition(businessData));
    } else {
      await saveBusinessDefinition({
        id: allBusinessDefinition?.[isArchive ? onEditArchived : onEdit]?.id,
        createdBy: allBusinessDefinition?.[isArchive ? onEditArchived : onEdit]?.createdBy,
        createdOn: allBusinessDefinition?.[isArchive ? onEditArchived : onEdit]?.createdOn,
        businessDefinitionName:
          watch('businessDefinitionName') ??
          allBusinessDefinition?.[isArchive ? onEditArchived : onEdit]?.businessDefinitionName,
        stageStatusData:
          allBusinessDefinition?.[isArchive ? onEditArchived : onEdit]?.stageStatusData,
        isArchive: allBusinessDefinition?.[isArchive ? onEditArchived : onEdit]?.isArchive ?? false,
        jobOption:
          allBusinessDefinition?.[isArchive ? onEditArchived : onEdit]?.jobOption ?? 'all_jobs',
      });
    }
    setOnEdit(-1);
    setOnEditArchived(-1);
    setBusinessData({ stageStatusData: [], businessDefinitionName: '' });
    methods.reset();
    setNotification(success('Business Definition added successfully'));
    fetchAllBusinessDefinition();
  };

  const setData = (data) => {
    const initialAllBusinessDefinition = allBusinessDefinition || [];
    const indexToUpdate = initialAllBusinessDefinition.findIndex((item) => item.id === data.id);
    if (indexToUpdate !== -1) {
      const updatedAllBusinessDefinition = [...initialAllBusinessDefinition];
      updatedAllBusinessDefinition[indexToUpdate] = data;
      setAllBusinessDefinition(updatedAllBusinessDefinition);
    } else {
      setAllBusinessDefinition([...initialAllBusinessDefinition, data]);
    }
    setBusinessData(data);
  };

  const deleteStage = (businessIndex: number, stageIndex: number) => {
    const updatedBusinessDefinitions = [...(allBusinessDefinition ?? [])];
    const businessDefinitionToUpdate = updatedBusinessDefinitions[businessIndex];
    if (
      businessDefinitionToUpdate &&
      stageIndex < businessDefinitionToUpdate.stageStatusData.length
    ) {
      const updatedStageStatusData = [...businessDefinitionToUpdate.stageStatusData];
      updatedStageStatusData.splice(stageIndex, 1);
      businessDefinitionToUpdate.stageStatusData = updatedStageStatusData;
      setAllBusinessDefinition(updatedBusinessDefinitions);
    }
  };

  const deleteStatus = (businessIndex: number, stageIndex: number, statusIndex: number) => {
    const updatedBusinessDefinitions = [...(allBusinessDefinition ?? [])];
    const businessDefinitionToUpdate = updatedBusinessDefinitions[businessIndex];
    if (
      businessDefinitionToUpdate &&
      stageIndex < businessDefinitionToUpdate.stageStatusData.length
    ) {
      const updatedStageStatusData = [...businessDefinitionToUpdate.stageStatusData];
      const stageDataToUpdate = updatedStageStatusData[stageIndex];
      if (stageDataToUpdate && statusIndex < stageDataToUpdate.selectedStatuses.length) {
        const updatedSelectedStatuses = [...stageDataToUpdate.selectedStatuses];
        updatedSelectedStatuses.splice(statusIndex, 1);
        stageDataToUpdate.selectedStatuses = updatedSelectedStatuses;
        businessDefinitionToUpdate.stageStatusData = updatedStageStatusData;
        setAllBusinessDefinition(updatedBusinessDefinitions);
      }
    }
  };

  const handleEditClick = (index, isArchive) =>
    isArchive ? setOnEditArchived(index) : setOnEdit(index);

  const handleDeleteClick = async (index, isArchive) => {
    isArchive
      ? await deleteByIdBusinessDefinition(archivedBusinessDefinitions?.[index].id ?? '')
      : await deleteByIdBusinessDefinition(notArchivedBusinessDefinitions?.[index].id ?? '');
    setNotification(success('Business Definition deleted successfully'));
    fetchAllBusinessDefinition();
  };

  const handleRename = (index) => {
    setIsRename(true);
    setBusinessName(allBusinessDefinition?.[index].businessDefinitionName ?? '');
    setJobOption(allBusinessDefinition?.[index].jobOption ?? '');
    setBusinessData(allBusinessDefinition?.[index]);
  };

  const handleArchive = async (index, isArchive) => {
    !isArchive
      ? await updateArchiveByIdBusinessDefinition(
          archivedBusinessDefinitions?.[index].id ?? '',
          isArchive
        )
      : await updateArchiveByIdBusinessDefinition(
          notArchivedBusinessDefinitions?.[index].id ?? '',
          isArchive
        );
    fetchAllBusinessDefinition();
  };

  const sortBU = (items?: IBusinessData[]) =>
    items?.sort((a, b) =>
      a?.displayOrder && b?.displayOrder ? a.displayOrder - b.displayOrder : 0
    );
  const archivedBusinessDefinitions = sortBU(
    allBusinessDefinition?.filter((businessDefinition) => businessDefinition.isArchive)
  );
  const notArchivedBusinessDefinitions = sortBU(
    allBusinessDefinition?.filter((businessDefinition) => !businessDefinition.isArchive)
  );
  const GetAddChildEntitiesButton = () => (
    <WsButton
      startIcon={<AddIcon />}
      variant="contained"
      onClick={() => setOpenBusinessModal(true)}
    >
      {'Add Business Definition'}
    </WsButton>
  );
  const onConfirmRearrange = async (reArrangedList: BUOrder[]) => {
    setLoader(true);
    setOpenReArrange(false);
    const req: BUUpdateOrderReq = {
      businessUnits: reArrangedList,
    };
    await updateBusinessDefinitionOrderById(req);
    setLoader(false);

    await fetchAllBusinessDefinition();
  };

  return (
    <>
      {allBusinessDefinition && allBusinessDefinition.length > 0 && !loader ? (
        <FormProvider {...methods}>
          <Grid container spacing={2} justifyContent="flex-end" p={4} columnGap={2}>
            <WsButton
              onClick={() => setOpenReArrange(true)}
              variant="outlined"
              startIcon={<UnfoldMoreOutlinedIcon />}
            >
              Rearrange
            </WsButton>
            <WsButton onClick={() => setOpenBusinessModal(true)} variant="contained">
              Add Business Definition
            </WsButton>
          </Grid>
          <Grid container direction="row" spacing={2}>
            {notArchivedBusinessDefinitions?.map((businessDefinition, cardIndex) => (
              <BusinessDefinitionCard
                key={cardIndex}
                businessDefinition={businessDefinition}
                cardIndex={cardIndex}
                onEdit={onEdit}
                onSubmit={onSubmit}
                setOnEdit={setOnEdit}
                handleEditClick={handleEditClick}
                handleDeleteClick={handleDeleteClick}
                deleteStage={deleteStage}
                deleteStatus={deleteStatus}
                handleToggle={handleToggle}
                openCardIndex={openCardIndex}
                setStageData={setStageData}
                setBusinessData={setBusinessData}
                setOpenSubStatus={setOpenSubStatus}
                setOpenStatusModal={setOpenStatusModal}
                setBusinessName={setBusinessName}
                setJobOption={setJobOption}
                handleArchive={handleArchive}
                setOpenBusiness={setOpenBusinessModal}
                handleRename={handleRename}
                onArchive={true}
                total={businessDefinition?.stageStatusData.length}
              />
            ))}
          </Grid>
          {!isEmpty(archivedBusinessDefinitions) && (
            <Grid item xs={12} mt={4}>
              <Label
                sx={{
                  fontWeight: 'bold',
                  minWidth: '100%',
                  ml: 2,
                  mb: 2,
                }}
              >
                Archived Business Definition
              </Label>
            </Grid>
          )}
          <Grid container direction="row" spacing={2}>
            {archivedBusinessDefinitions &&
              archivedBusinessDefinitions?.map((businessDefinition, cardIndex) => (
                <BusinessDefinitionCard
                  key={cardIndex}
                  businessDefinition={businessDefinition}
                  cardIndex={cardIndex}
                  onEdit={onEditArchived}
                  onSubmit={onSubmit}
                  setOnEdit={setOnEdit}
                  handleEditClick={handleEditClick}
                  handleDeleteClick={handleDeleteClick}
                  deleteStage={deleteStage}
                  deleteStatus={deleteStatus}
                  handleToggle={handleToggle}
                  openCardIndex={openCardIndex}
                  setStageData={setStageData}
                  setBusinessData={setBusinessData}
                  setOpenSubStatus={setOpenSubStatus}
                  setOpenStatusModal={setOpenStatusModal}
                  setBusinessName={setBusinessName}
                  setJobOption={setJobOption}
                  handleArchive={handleArchive}
                  setOpenBusiness={setOpenBusinessModal}
                  handleRename={handleRename}
                  onArchive={false}
                  total={businessDefinition?.stageStatusData.length}
                />
              ))}
          </Grid>
        </FormProvider>
      ) : loader ? (
        <Box textAlign="center" pt={15} px={4}>
          <Loader />
        </Box>
      ) : (
        <Grid item mt={15}>
          <NoDataFoundCard
            text="Create a foundational structure by defining stages to structure your data better"
            icon={<EmptyTeamsAndUsersIcon />}
            subElement={<Box m={2}>{<GetAddChildEntitiesButton />}</Box>}
          />
        </Grid>
      )}

      {(openStatusModal || openSubStatus || openBusinessModal) && (
        <StageStatusContextProvider>
          <BusinessStageStatusPopUp
            setOpenStatusModal={(action: boolean) => setOpenStatusModal(action)}
            isOpenModal={openStatusModal}
            setOpenSubStatus={(action: boolean) => {
              setOnEdit(onEdit);
              setOpenSubStatus(action);
            }}
            isSubStatusOpenModal={openSubStatus}
            setOpenBusiness={(action: boolean) => setOpenBusinessModal(action)}
            isBusinessOpenModal={openBusinessModal}
            businessData={(data: IBusinessData) => setData(data)}
            setName={(name) => setBusinessName(name)}
            businessName={businessName}
            attachBusiness={businessData}
            stageValues={stageData}
            setStageData={setStageData}
            setJobOptions={(jobOption) => setJobOption(jobOption)}
            jobOption={jobOption}
            isRename={isRename}
            setIsRename={setIsRename}
            fetchAll={fetchAllBusinessDefinition}
          />
        </StageStatusContextProvider>
      )}
      {allBusinessDefinition && allBusinessDefinition.length > 0 && openReArrange && (
        <OrderBUDefinition
          data={allBusinessDefinition}
          onSave={onConfirmRearrange}
          open={openReArrange}
          onClose={() => setOpenReArrange(!openReArrange)}
        />
      )}
    </>
  );
};

export default CustomizeBusinessDefinition;
